import { DateFormatStore } from '@macs-frontend/core/store/date-format.store';
import { appSettings } from '@macs-frontend/core/utils/app-settings';
import { fns } from '@macs-frontend/core/utils/date-fns';
import { AppDateTime, parseAppDateTime } from '@macs-frontend/core/utils/datetime';
import { E, F, O, RA, RR } from '@macs-frontend/core/utils/fp-ts';
import { isoUUID, UUID, uuid4 } from '@macs-frontend/core/utils/uuid';
import { CSCalculations } from '@macs-frontend/features/company-management/projects/classes/channels-suggestions-calculations';
import {
  ActionTeam,
  ActionTeamsTotalsDictionary,
  ActionTeamTotals,
  BillingMethodsEnum,
  BudgetItemForServer,
  BudgetSplit,
  BudgetTypes,
  Channel,
  ChannelGroup,
  ChannelGroupSelector,
  ChannelSelector,
  emptyTargetGroupFilter,
  ExpenseForm,
  ExpenseGroupForm,
  FinancialUtils,
  InvoiceStatuses,
  Prices,
  Project,
  ProjectBudgetTypes,
  ProjectStatuses,
  ProjectTask,
  ProjectTaskGroup,
  TeamMember,
  TeamTaskForm,
} from '@macs-frontend/web-macs/shared';
import { ActionTeamForServer } from '@macs-frontend/web-macs/shared/models/action-team-for-server';
import { FormArrayState, FormGroupState, unbox } from 'ngrx-forms';
import { WarehouseForServer } from '../../company-management/action-team/classes/action-team-calculations';
import { MTCalculations } from '../../company-management/projects/classes/materials-management-calculations';
import { BudgetItemForm } from '../../company-management/projects/models/budget-item-form';
import {
  ChannelForm,
  ChannelGroupForm,
  ChannelsForm,
  ChannelTeamForm,
} from '../../company-management/projects/reducers/channels';
import { MTMaterialGroupForm } from '../../company-management/projects/reducers/materials-management';
import { EditProjectForm } from '../../company-management/projects/reducers/project';
import { WarehouseForm } from '../../company-management/projects/reducers/warehouse';
import { RatesheetValueTypes } from '../../company-management/ratesheet/enums/ratesheet-value-types.enum';
import { GeneralInfoForm } from '../../manage-project-shared/reducers/general-info.reducer';
import { BudgetItemTypes } from '../models/budget-item';
import { ChannelGroupSelectorServer } from '../models/channel-group-selector.model';
import { InvoiceTypes } from '../models/invoice.model';
import { Ratesheet } from '../models/ratesheet.model';
import { Role } from '../models/role';
import {
  Age,
  AgeRanges,
  FilterGender,
  Genders,
  TargetGroupFilter,
} from '../models/target-group-filter';
import { ActionTeamsForServer } from './../models/action-team-for-server';
import { sortByCreatedAt } from './date';

export interface TargetGroupFiltersForServer {
  [id: string]: {
    name: string;
    ages: { age: string }[];
    genders: { gender: Genders }[];
  };
}

export interface EstimateForServer {
  subject: string;
  project?: UUID | null;
  isMain?: boolean;
  status: InvoiceStatuses;
  client?: UUID | null;
  type: InvoiceTypes;
  draft?: boolean;
  currency?: UUID;
  uuid?: UUID;
  dueDateType?: string;
  invoiceId?: string;
}

export interface TaskForServer {
  billable: boolean;
  billingRatePerHour: number;
  budgetFee: number;
  budgetHours: number;
  uuid: UUID;
  originalTask: string;
  budgetItem?: BudgetItemForServer;
}

export interface TaskGroupForServer {
  uuid: UUID;
  originalTaskGroup: UUID;
  billable: boolean;
  budgetHours: number;
  budgetFee: number;
  billingRatePerHour: number;
  tasks: RR.ReadonlyRecord<string /* uuid */, TaskForServer>;
  description: string;
  newTaskName: string;
  createdAt?: AppDateTime;
}

export interface GenderForServer {
  gender: Genders;
}

export interface ProjectPreparationPayload {
  editProjectForm: FormGroupState<EditProjectForm>;
  generalInfoForm: FormGroupState<GeneralInfoForm>;
  teamTaskForm: FormGroupState<TeamTaskForm>;
  project: Project;
  channelsForm: FormGroupState<ChannelsForm>;
  materialsManagementForm: FormArrayState<MTMaterialGroupForm>;
  selectedChannelGroups: ChannelGroup[];
  selectedChannels: Channel[];
  materialsTotalPrices: Prices;
  totalBudget: number;
  actionTeamsTotals: ActionTeamsTotalsDictionary;
  actionTeamsGrandTotals: ActionTeamTotals;
  grossMargin: number;
  actionTeams: ActionTeam[];
  budgetItemsForm: BudgetItemForm[];
  defaultStaffingCostBudgetItemForm: BudgetItemForm;
  defaultTeamCostBudgetItemForm: BudgetItemForm;
  warehousesForm: WarehouseForm[];
  warehousesRateshetId: string;
  expenses: ExpenseForm[];
  expenseGroups: ExpenseGroupForm[];
}

export class ProjectCalculations {
  static prepareActionTeamsForServer(
    actionTeams: ActionTeam[],
    actionTeamsTotals: ActionTeamsTotalsDictionary,
    budgetItemsForm: BudgetItemForm[],
  ): ActionTeamsForServer {
    const actionsTeamsForServer: ActionTeamsForServer = {};
    actionTeams.forEach(team => {
      const data = {
        billablePrice: FinancialUtils.toCents(team.billablePrice),
        unitPrice: FinancialUtils.toCents(team.unitPrice),
        actions: team.actions,
        totalCost: actionTeamsTotals[isoUUID.unwrap(team.uuid)]
          ? FinancialUtils.toCents(
              actionTeamsTotals[isoUUID.unwrap(team.uuid)].totalBillablePrice,
            )
          : 0,
        realActionsQty: actionTeamsTotals[isoUUID.unwrap(team.uuid)]
          ? actionTeamsTotals[isoUUID.unwrap(team.uuid)].actions
          : 0,
        realTeamCost: actionTeamsTotals[isoUUID.unwrap(team.uuid)]
          ? FinancialUtils.toCents(
              actionTeamsTotals[isoUUID.unwrap(team.uuid)].totalUnitPrice,
            )
          : 0,
        extraCost: FinancialUtils.toCents(team.extraCost),
      } as Partial<ActionTeamForServer>;

      if (budgetItemsForm && team.budgetItem) {
        const budgetItem = budgetItemsForm.find(
          // @ts-expect-error
          ({ uuid }) => team.budgetItem.uuid === uuid,
        );
        if (budgetItem) {
          data.budgetItem = this.prepareBudgetItemForServer(budgetItem);
        }
      }

      if (budgetItemsForm && team.feesBudgetItem) {
        const budgetItem = budgetItemsForm.find(
          // @ts-expect-error
          ({ uuid }) => team.feesBudgetItem.uuid === uuid,
        );
        if (budgetItem) {
          data.feesBudgetItem = this.prepareBudgetItemForServer(budgetItem);
        }
      }

      actionsTeamsForServer[isoUUID.unwrap(team.uuid)] = data as ActionTeamForServer;
    });
    return actionsTeamsForServer;
  }

  static prepareEstimateForServer(
    project: Project,
    name: string,
    isDraft: boolean,
  ): EstimateForServer {
    return {
      subject: 'Main estimate for ' + name,
      project: project.uuid,
      client: project.client.uuid,
      status: InvoiceStatuses.DRAFT,
      isMain: true,
      type: InvoiceTypes.ESTIMATE,
      draft: isDraft,
      currency: project.company?.currency?.uuid,
    };
  }

  static prepareTargetGroupFilterForServer(
    targetGroupFilter: TargetGroupFilter,
  ): TargetGroupFiltersForServer {
    const targetGroupFilterForServer: TargetGroupFiltersForServer = {};
    targetGroupFilterForServer[isoUUID.unwrap(targetGroupFilter.uuid || uuid4())] = {
      ...targetGroupFilter,
      genders: this.prepareTargetGroupGendersForServer(targetGroupFilter.genders),
      name: targetGroupFilter.name,
    };
    return targetGroupFilterForServer;
  }

  static createNameForTargetGroupFilter(
    targetGroupFilters: TargetGroupFilter[],
    index = 1,
  ): string {
    return !targetGroupFilters.find(group => group.name === `Filter ${index}`)
      ? `Filter ${index}`
      : // eslint-disable-next-line no-param-reassign
        this.createNameForTargetGroupFilter(targetGroupFilters, ++index);
  }

  static prepareBudgetSplitsForServer(budgetSplits: BudgetSplit[]) {
    const budgetSplitsObject: any = {};
    const counter = 0;
    budgetSplits.forEach(t => {
      const uuid = t.uuid || uuid4();
      budgetSplitsObject[isoUUID.unwrap(uuid)] = {
        ...t,
        amount: FinancialUtils.toCents(t.amount),
      };
      delete budgetSplitsObject[isoUUID.unwrap(uuid)].uuid;
    });
    return budgetSplitsObject;
  }

  static prepareGroupsSelectors(
    selectedChannelGroups: ChannelGroup[],
    channelGroupSelectors: ChannelGroupSelector[],
  ): { readonly [uuid: string]: ChannelGroupSelectorServer } {
    return F.pipe(
      channelGroupSelectors,
      RA.reduce(
        {} as { [uuid: string]: ChannelGroupSelectorServer },
        (acc, groupSelector) => {
          const group = selectedChannelGroups.find(
            group => group.uuid === groupSelector.channelGroup.uuid,
          );

          acc[isoUUID.unwrap(groupSelector.uuid)] = {
            position: groupSelector.position,
            channelGroup: groupSelector.channelGroup.uuid,
            isSelected: !!group,
          };

          return acc;
        },
      ),
    );
  }

  static prepareChannelSelectors(
    selectedChannels: (Channel | ChannelForm)[],
    channelSelectors: ChannelSelector[], // channel selectors from current project
    channelsForm?: ChannelsForm, // contains channelGroups: EntityState<ChannelGroupForm>;
    budgetItemsForm?: BudgetItemForm[],
  ) {
    const retObj: any = {};

    channelSelectors
      .map((channelSelector: ChannelSelector) => {
        const channel = selectedChannels.find(
          channel => channel.uuid === channelSelector.channel.uuid,
        );

        // eslint-disable-next-line functional/no-let
        let contactsPerAction = channelSelector.contactsPerAction;
        if (channelsForm) {
          const channelForm = CSCalculations.findChannelForm(
            channelSelector.channel.uuid,
            channelsForm,
          );
          contactsPerAction = F.pipe(
            channelForm,
            O.fold(
              () => 0,
              ({ contactsPerAction }) => contactsPerAction,
            ),
          );
        }

        if (!channel) {
          return {
            ...channelSelector,
            channel: channelSelector.channel.uuid,
            isSelected: false,
            contactsPerAction,
          };
        }

        // eslint-disable-next-line functional/no-let
        let actions = null;
        if (channelsForm) {
          const group =
            channelsForm.channelGroups.entities[
              isoUUID.unwrap(channel.channelGroup.uuid)
            ];
          const selectedChannel = group?.channels.find(
            findChannel => findChannel.uuid === channel.uuid,
          );
          if (selectedChannel) {
            actions = selectedChannel.actions;
          }
        }

        const budgetItemForm = F.pipe(
          budgetItemsForm || [],
          RA.findFirstMap(item =>
            item.type === BudgetItemTypes.FEES && item.uuid === channel.uuid
              ? O.some(item)
              : O.none,
          ),
        );

        const budgetItem = F.pipe(
          budgetItemForm,
          O.fold(() => null, F.flow(this.prepareBudgetItemForServer)),
        );

        return {
          ...channelSelector,
          channel: channelSelector.channel.uuid,
          isSelected: true,
          actions,
          budgetItem,
          contactsPerAction,
        };
      })
      .map(
        g =>
          (retObj[isoUUID.unwrap(g.uuid)] = g) &&
          delete retObj[isoUUID.unwrap(g.uuid)].uuid,
      );

    return retObj;
  }

  static prepareAgeRangesForServer(ages: Age[]): { age: string }[] {
    const selectedAges: Age[] = ages.filter(ageRange => ageRange.selected);
    return (selectedAges.length ? selectedAges : ages).map(ageRange => ({
      age: ageRange.age === AgeRanges.age_65_more ? '65+' : ageRange.age,
    }));
  }

  static prepareTasksForServer(
    tasks: readonly ProjectTask[],
    budgetItemsForm?: readonly BudgetItemForm[],
    calcBudgetFee = false,
  ): TaskForServer[] {
    return tasks
      .slice()
      .sort(sortByCreatedAt)
      .map((task: ProjectTask) => {
        const budgetFee = calcBudgetFee
          ? FinancialUtils.roundToCents(task.budgetHours * task.billingRatePerHour)
          : task.budgetFee;
        // @ts-expect-error
        const data = {
          ...task,
          originalTask: task.originalTask.uuid,
          billingRatePerHour: FinancialUtils.toCents(task.billingRatePerHour),
          budgetFee: FinancialUtils.toCents(budgetFee),
          budgetItem: null,
        } as TaskForServer;

        if (budgetItemsForm && task.budgetItem && task.budgetItem.uuid) {
          const budgetItem = budgetItemsForm.find(
            // @ts-expect-error
            item => item.uuid === task.budgetItem.uuid,
          );
          if (budgetItem) {
            data.budgetItem = this.prepareBudgetItemForServer(budgetItem);
          }
        }

        return data;
      });
  }

  static prepareTaskGroupsForServer(
    taskGroups: ProjectTaskGroup[],
    budgetItemsForm?: BudgetItemForm[],
    calcBudgetFee = false,
  ): TaskGroupForServer[] {
    return taskGroups
      .map((taskGroup: ProjectTaskGroup) => {
        // eslint-disable-next-line functional/no-let
        let budgetItem: BudgetItemForm;
        const hasBillableTasks = taskGroup.tasks.some(task => task.billable);
        const isTaskGroupSingleItem =
          hasBillableTasks &&
          calcBudgetFee &&
          budgetItemsForm &&
          taskGroup.budgetItem &&
          taskGroup.budgetItem.uuid &&
          !taskGroup.tasks.some(task => !!+task.budgetHours);

        if (isTaskGroupSingleItem) {
          // @ts-expect-error
          budgetItem = budgetItemsForm.find(
            // @ts-expect-error
            item => item.uuid === taskGroup.budgetItem.uuid,
          );
        }
        const budgetFee = calcBudgetFee
          ? FinancialUtils.roundToCents(
              ProjectCalculations.calcTaskGroupBillablePrice(taskGroup) *
                taskGroup.budgetHours,
            )
          : taskGroup.budgetFee;
        return {
          ...taskGroup,
          originalTaskGroup: taskGroup.originalTaskGroup.uuid,
          tasks: F.pipe(
            this.prepareTasksForServer(taskGroup.tasks, budgetItemsForm, calcBudgetFee),
            RA.reduce({} as Record<string, TaskForServer>, (acc, task) => {
              acc[task.uuid.toString()] = task;

              return acc;
            }),
          ),
          budgetFee: FinancialUtils.toCents(budgetFee),
          billingRatePerHour: FinancialUtils.toCents(taskGroup.billingRatePerHour),
          // @ts-expect-error
          budgetItem: budgetItem ? this.prepareBudgetItemForServer(budgetItem) : null,
          billable: isTaskGroupSingleItem || hasBillableTasks,
        };
      })
      .sort(sortByCreatedAt);
  }

  static prepareTargetGroupGendersForServer(genders: FilterGender[]): GenderForServer[] {
    return genders
      .filter((filterGender: FilterGender) => filterGender.selected)
      .map((filterGender: FilterGender) => ({ gender: filterGender.gender }));
  }

  static prepareFiltersGendersForClient(filterGenders: FilterGender[]): FilterGender[] {
    return emptyTargetGroupFilter.genders.map((emptyGender: FilterGender) => ({
      ...emptyGender,
      selected: !!filterGenders.find(
        (filterGender: FilterGender) => filterGender.gender === emptyGender.gender,
      ),
    }));
  }

  static getApprovedRemainingBudget(project?: Project | null): number {
    if (!project) {
      return 0;
    }
    const remainingBudget = ProjectCalculations.getRemainingBudget(project);

    return project?.confirmedEstimates?.length ? remainingBudget : 0;
  }

  static getRemainingBudget(project?: Project | null): number {
    if (!project) {
      return 0;
    }

    const budgetIsHours = ProjectCalculations.isProjectBudgetInHours(project);
    const isNoBillingMethodSelected =
      ProjectCalculations.isNoBillingMethodSelected(project);

    if (budgetIsHours) {
      const remainingHours = project.approvedRemainingHours;

      return (
        (isNoBillingMethodSelected
          ? remainingHours
          : project.remainingBudget || remainingHours) || 0
      );
    }
    return project.remainingBudget || 0;
  }

  static getApprovedBudget(project?: Project | null): number {
    if (!project) {
      return 0;
    }

    return project?.hasConfirmedEstimates ? ProjectCalculations.getBudget(project) : 0;
  }

  static getBudget(project?: Project | null): number {
    if (!project) {
      return 0;
    }

    const budgetIsHours = ProjectCalculations.isProjectBudgetInHours(project);
    const isNoBillingMethodSelected =
      ProjectCalculations.isNoBillingMethodSelected(project);

    if (budgetIsHours && (!project.totalFees || isNoBillingMethodSelected)) {
      return project.approvedTotalHours || 0;
    }

    if (project.budgetType === ProjectBudgetTypes.FixedFee) {
      return project.fixedFee || 0;
    }

    return project.totalFees || 0;
  }

  static prepareProjectForServer(payload: ProjectPreparationPayload) {
    const {
      teamTaskForm,
      project,
      generalInfoForm,
      editProjectForm,
      channelsForm,
      materialsManagementForm,
      actionTeams,
      actionTeamsTotals,
      actionTeamsGrandTotals,
      selectedChannels,
      selectedChannelGroups,
      totalBudget,
      grossMargin,
      materialsTotalPrices,
      budgetItemsForm,
      defaultStaffingCostBudgetItemForm,
      defaultTeamCostBudgetItemForm,
      warehousesForm,
      warehousesRateshetId,
      expenseGroups,
      expenses,
    } = payload;
    const channelsFormValue = channelsForm.value;
    const teamTaskFormValue = teamTaskForm.value;
    const calcBudgetFee =
      (teamTaskFormValue.budget === BudgetTypes.TOTAL_BUDGET_AND_HOURS_PER_TASK ||
        teamTaskFormValue.budget === BudgetTypes.HOURS_PER_TASK) &&
      teamTaskFormValue.billingMethod === BillingMethodsEnum.TASK_HOURLY_RATE;

    const newRoleUnits = teamTaskForm.controls.teamMembers.value
      .filter(
        member =>
          member.roleUnitId &&
          !project.roleUnits.find(
            unit => unit.user && unit.user.uuid === member.user.uuid,
          ),
      )
      .map(member => ({
        role: member.roleUnitId,
        user: member.user.uuid,
      }));
    const allRoleUnits = [
      ...project.roleUnits
        .map(roleUnit => {
          // @ts-expect-error
          const m: TeamMember = teamTaskForm.controls.teamMembers.value.find(
            u => u.user.uuid === roleUnit.user.uuid,
          );
          if (m && m.roleUnitId !== roleUnit.role.uuid) {
            return {
              role: m.roleUnitId,
              user: roleUnit.user.uuid,
            };
          } else {
            return {
              role: roleUnit.role.uuid,
              user: roleUnit.user.uuid,
            };
          }
        })
        .filter(v => v),
      ...newRoleUnits,
    ];
    return {
      startDate: generalInfoForm.value.startDate,
      name: editProjectForm.value.name,
      label: editProjectForm.value.label,
      client: generalInfoForm.value.clientId,
      number: generalInfoForm.value.number,
      description: generalInfoForm.value.description,
      endDate: generalInfoForm.value.endDate,
      projectGroup: generalInfoForm.value.projectGroupId,
      projectId: generalInfoForm.value.projectId,
      contractProjectId: generalInfoForm.value.contractProjectId || null,
      tags: unbox(generalInfoForm.value.tags).map(tag => tag.uuid),
      starred: project.starred,
      totalFees: teamTaskFormValue.totalFees,
      status:
        (teamTaskFormValue.tasks.ids.length > 0 ||
          teamTaskFormValue.taskGroups.ids.length > 0 ||
          (teamTaskFormValue.budget === BudgetTypes.TOTAL_BUDGET_AND_HOURS_PER_TASK &&
            teamTaskFormValue.totalFees)) &&
        teamTaskFormValue.teamMembers.length > 0 &&
        (teamTaskFormValue.budget !== BudgetTypes.NO_BUDGET_SELECTED ||
          teamTaskFormValue.nonBillableBudget !== BudgetTypes.NO_BUDGET_SELECTED)
          ? ProjectStatuses.ACTIVE
          : ProjectStatuses.DRAFT,
      ratesheet: teamTaskFormValue.ratesheetId,
      logoFilename: generalInfoForm.value.logo,
      timeAndMaterials: {
        ...project.timeAndMaterials,
        budget: teamTaskFormValue.budget,
        billingMethod: teamTaskFormValue.billingMethod,
        includesBillableAndNonBillableProjectExpenses:
          teamTaskFormValue.includesBillableAndNonBillableProjectExpenses,
        sendEmailExceedPercent: teamTaskFormValue.sendEmailExceedPercent,
        sendEmailExceed: teamTaskFormValue.sendEmailExceed,
      },
      budgetType: teamTaskFormValue.budgetType,
      trackTasksBy: teamTaskFormValue.trackTasksBy,
      nonBillableBudget: teamTaskFormValue.nonBillableBudget,
      fixedFee: FinancialUtils.toCents(teamTaskFormValue.fixedFee),
      nettoMarginTarget: teamTaskFormValue.nettoMarginTarget,
      grossMarginTarget: teamTaskFormValue.grossMarginTarget,
      teamMembers: teamTaskFormValue.teamMembers.map(teamMember => ({
        user: teamMember.user.uuid,
        budgetHours: teamMember.budgetHours,
        billableRatePerHour: FinancialUtils.toCents(teamMember.billableRatePerHour),
        useUserRate: teamMember.useUserRate,
      })),
      ...(allRoleUnits.length > 0 && {
        roleUnits: allRoleUnits.filter(unit =>
          teamTaskFormValue.teamMembers.find(m => m.user.uuid === unit.user),
        ),
      }),
      tasks: F.pipe(
        ProjectCalculations.prepareTasksForServer(
          // @ts-expect-error
          Object.values(teamTaskFormValue.tasks.entities),
          budgetItemsForm,
          calcBudgetFee,
        ),
        RA.reduce({} as Record<string, TaskForServer>, (acc, task) => {
          acc[task.uuid.toString()] = task;

          return acc;
        }),
      ),
      taskGroups: F.pipe(
        ProjectCalculations.prepareTaskGroupsForServer(
          // @ts-expect-error
          Object.values(teamTaskFormValue.taskGroups.entities),
          budgetItemsForm,
          calcBudgetFee,
        ),
        RA.reduce({} as Record<string, TaskGroupForServer>, (acc, taskGroup) => {
          acc[taskGroup.uuid.toString()] = taskGroup;

          return acc;
        }),
      ),
      totalHours: teamTaskFormValue.totalHours,
      hourlyRate: FinancialUtils.toCents(teamTaskFormValue.hourlyRate),
      briefingInfoSettings: {
        budget:
          channelsFormValue.budget && FinancialUtils.toCents(channelsFormValue.budget),
        budgetSplits: ProjectCalculations.prepareBudgetSplitsForServer(
          channelsFormValue.budgetSplits,
        ),
        targetContacts: channelsFormValue.targetToContactsPercent,
        totalContacts: channelsFormValue.totalContacts,
        productType: channelsFormValue.briefingInfoSettings.productType.uuid,
        rateSheet: channelsFormValue.briefingInfoSettings.rateSheet,
      },
      actionTeams: ProjectCalculations.prepareActionTeamsForServer(
        actionTeams,
        actionTeamsTotals,
        budgetItemsForm,
      ),
      projectMaterialGroups: MTCalculations.prepareMaterialsForServer(
        materialsManagementForm.value,
        budgetItemsForm,
      ),
      channelGroupSelectors: ProjectCalculations.prepareGroupsSelectors(
        selectedChannelGroups,
        project.channelGroupSelectors,
      ),
      channelSelectors: ProjectCalculations.prepareChannelSelectors(
        selectedChannels,
        project.channelSelectors,
        channelsFormValue,
        budgetItemsForm,
      ),

      // action planning
      costContacts: FinancialUtils.toCents(actionTeamsGrandTotals.ccBillable),
      currentTotalActions: actionTeamsGrandTotals.actions,
      currentTotalBudget: FinancialUtils.toCents(totalBudget),
      currentTotalContacts: actionTeamsGrandTotals.contacts,
      currentTotalCost: FinancialUtils.toCents(actionTeamsGrandTotals.totalBillablePrice),
      materialsTotalBillablePrice: materialsTotalPrices.billablePrice,
      materialsTotalUnitPrice: materialsTotalPrices.unitPrice,
      grossMargin,
      draft: project.draft,
      defaultStaffingCostBudgetItem: this.prepareBudgetItemForServer(
        defaultStaffingCostBudgetItemForm,
      ),
      defaultTeamCostBudgetItem: this.prepareBudgetItemForServer(
        defaultTeamCostBudgetItemForm,
      ),
      warehouses: this.prepareWarehousesForServer(warehousesForm, budgetItemsForm),
      warehousesRateshet: warehousesRateshetId,

      expenses: this.prepareExpenseForServer(expenses),
      expenseGroups: expenseGroups.map(group => ({
        ...group,
        description: group.description || group.name,
        expenses: this.prepareExpenseForServer(group.expenses),
      })),
    };
  }

  static prepareBudgetItemForServer = (
    budgetItem: BudgetItemForm,
  ): BudgetItemForServer => ({
    unitPrice: budgetItem.unitPrice || 0,
    discount: budgetItem.discount || 0,
    billablePrice: budgetItem.billablePrice || 0,
  });

  static updateRoleWithRsData(ratesheet: Ratesheet, roleUUID?: UUID): Role {
    const targetRole = ratesheet.ratesheetValues
      .filter(
        value => value.type === RatesheetValueTypes.Internal && value.ratesheetInternal,
      )
      .map(rsValue => {
        const ratesheetRole = rsValue.ratesheetInternal;
        // @ts-expect-error
        const { role, billablePrice } = ratesheetRole;

        return {
          ...role,
          billablePrice: FinancialUtils.fromCents(billablePrice) || null,
        } as Role;
      })
      .find(rsRole => rsRole.uuid === roleUUID);
    return {
      ...targetRole,
      // @ts-expect-error
      billablePrice: targetRole ? targetRole.billablePrice : null,
    };
  }

  static updateTeamMembersWithRsData(
    ratesheet: Ratesheet,
    members: TeamMember[],
  ): TeamMember[] {
    return members.map(member => {
      const updatedRole = ProjectCalculations.updateRoleWithRsData(
        ratesheet,
        member.roleUnitId,
      );
      return {
        ...member,
        billableRatePerHour:
          (updatedRole && updatedRole.billablePrice) ||
          (!ratesheet.uuid &&
            member.user &&
            FinancialUtils.fromCents(member.user.currentBillableRate)) ||
          null,
      };
    });
  }

  static updateTaskWithRsData(ratesheet: Ratesheet, task: ProjectTask) {
    if (ratesheet.uuid) {
      const targetTask = ratesheet.ratesheetValues
        .filter(value => value.type === RatesheetValueTypes.Tasks && value.ratesheetTask)
        .find(value => value.ratesheetTask?.task.uuid === task.originalTask.uuid);
      return {
        ...task,
        billingRatePerHour: targetTask
          ? // @ts-expect-error
            FinancialUtils.fromCents(targetTask.ratesheetTask.billablePrice)
          : null,
      };
    }

    return {
      ...task,
      billingRatePerHour:
        FinancialUtils.fromCents(task.originalTask.defaultHourlyRate) || null,
    };
  }

  static updateTasksWithRsData(
    ratesheet: Ratesheet,
    tasks: ProjectTask[],
  ): ProjectTask[] {
    // @ts-expect-error
    return tasks.map(task => {
      if (ratesheet.uuid) {
        return ProjectCalculations.updateTaskWithRsData(ratesheet, task);
      }
      return {
        ...task,
        billingRatePerHour:
          FinancialUtils.fromCents(task.originalTask.defaultHourlyRate) || null,
      };
    });
  }

  static calcTaskGroupBillablePrice(taskGroup: ProjectTaskGroup): number {
    if (
      taskGroup.tasks &&
      !taskGroup.tasks.some(task => !!+task.budgetHours) &&
      taskGroup.tasks.length
    ) {
      const billableTasks: ProjectTask[] = taskGroup.tasks.filter(task => task.billable);
      const avgRate =
        billableTasks.reduce((sum, task) => sum + +task.billingRatePerHour, 0) /
        billableTasks.length;
      return !isNaN(avgRate) ? avgRate : 0;
    }
    return 0;
  }

  static isTaskGroupValidForNewBudgetItem(taskGroup: ProjectTaskGroup): boolean {
    const hasTasksHours = taskGroup.tasks.some(task => !!+task.budgetHours);
    const alreadyHasBudgetItem = taskGroup.budgetItem && taskGroup.budgetItem.uuid;
    return !alreadyHasBudgetItem && !hasTasksHours;
  }

  // @ts-expect-error
  static getBillingMethod(budget: BudgetTypes): BillingMethodsEnum {
    switch (budget) {
      case BudgetTypes.TOTAL_BUDGET_AND_HOURS_PER_TASK:
      case BudgetTypes.HOURS_PER_TASK: {
        return BillingMethodsEnum.TASK_HOURLY_RATE;
      }

      case BudgetTypes.HOURS_PER_PERSON: {
        return BillingMethodsEnum.PERSON_HOURLY_RATE;
      }

      case BudgetTypes.TOTAL_PROJECT_HOURS: {
        return BillingMethodsEnum.PROJECT_HOURLY_RATE;
      }
    }
  }

  static prepareWarehousesForServer(
    warehouses: WarehouseForm[],
    budgetItems: BudgetItemForm[],
  ): WarehouseForServer[] {
    return warehouses
      .filter(warehouse => !!warehouse.name)
      .map((warehouse: WarehouseForm) => {
        const warehouseForServer: WarehouseForServer = {
          ...warehouse,
          unitPrice: FinancialUtils.toCents(warehouse.unitPrice),
          billablePrice: FinancialUtils.toCents(warehouse.billablePrice),
          amount: FinancialUtils.toCents(warehouse.amount),
        };
        if (warehouse.budgetItem) {
          const budgetItem = budgetItems.find(
            // @ts-expect-error
            item => item.uuid === warehouse.budgetItem.uuid,
          );
          warehouseForServer.budgetItem = ProjectCalculations.prepareBudgetItemForServer(
            // @ts-expect-error
            budgetItem,
          );
        }
        return warehouseForServer;
      });
  }

  static prepareExpenseForServer(
    expenses: ExpenseForm[],
    // @ts-expect-error
    groupId: string = null,
  ): ExpenseForm[] {
    return expenses.map(expense => ({
      ...expense,
      billableRate: expense.billablePrice,
      expenseUnitPrice: expense.unitPrice,
      expenseGroup: groupId,
      amount: FinancialUtils.toCents(expense.amount),
    }));
  }

  static getActionTeamByUuid(
    groups: ChannelGroupForm[],
    uuid: UUID,
  ): {
    channel: ChannelForm;
    group: ChannelGroupForm;
    actionTeam: ChannelTeamForm;
  } {
    // eslint-disable-next-line functional/no-let
    let targetChannel: ChannelForm;
    // eslint-disable-next-line functional/no-let
    let targetGroup: ChannelGroupForm;
    // eslint-disable-next-line functional/no-let
    let targetTeam: ChannelTeamForm;
    groups.forEach((channelGroup: ChannelGroupForm) => {
      channelGroup.channels.forEach((channel: ChannelForm) => {
        // @ts-expect-error
        channel.actionTeams.forEach((team: ChannelTeamForm) => {
          if (team.feesBudgetItem && unbox(team.feesBudgetItem).uuid === uuid) {
            targetTeam = team;
            targetChannel = channel;
            targetGroup = channelGroup;
          }
        });
      });
    });
    return {
      // @ts-expect-error
      channel: targetChannel,
      // @ts-expect-error
      group: targetGroup,
      // @ts-expect-error
      actionTeam: targetTeam,
    };
  }

  static isProjectBudgetInHours(project?: Project | null): boolean {
    if (!project) {
      return false;
    }

    const hoursBudgetTypes = [
      BudgetTypes.HOURS_PER_PERSON,
      BudgetTypes.HOURS_PER_TASK,
      BudgetTypes.TOTAL_PROJECT_HOURS,
      BudgetTypes.TOTAL_BUDGET_AND_HOURS_PER_TASK,
    ];

    switch (project.budgetType) {
      case ProjectBudgetTypes.FixedFee: {
        return false;
      }
      case ProjectBudgetTypes.NonBillable: {
        return hoursBudgetTypes.includes(project.nonBillableBudget as BudgetTypes);
      }
      case ProjectBudgetTypes.TimeMaterials: {
        return hoursBudgetTypes.includes(project.timeAndMaterials.budget as BudgetTypes);
      }
    }
    return false;
  }

  static isNoBillingMethodSelected(project?: Project | null): boolean {
    return (
      project?.timeAndMaterials?.billingMethod ===
      BillingMethodsEnum.NO_BILLING_METHOD_SELECTED
    );
  }

  static showBudgetMoney(project?: Project | null): boolean {
    if (!project) {
      return false;
    }
    return (
      !!project.totalFees &&
      !(
        ProjectCalculations.isProjectBudgetInHours(project) &&
        ProjectCalculations.isNoBillingMethodSelected(project)
      )
    );
  }

  static showRemainingBudgetMoney(project?: Project | null): boolean {
    if (!project) {
      return false;
    }
    return (
      !!project.totalFees &&
      !(
        ProjectCalculations.isProjectBudgetInHours(project) &&
        ProjectCalculations.isNoBillingMethodSelected(project)
      )
    );
  }

  static getEstimatedAbsoluteMargin(project: Project) {
    return FinancialUtils.calcAbsoluteMargin(
      project.confirmedEstimatesAmount,
      project.confirmedEstimatesCost,
    );
  }

  static getEstimatedMargin(project: Project) {
    return FinancialUtils.calcMargin(
      project.confirmedEstimatesAmount,
      project.confirmedEstimatesCost,
    );
  }

  static getActualdMargin(project: Project) {
    return FinancialUtils.calcMargin(project.actualSellPrice, project.actualCostPrice, 0);
  }
}

export const calcProjectNameTitle = (
  dateFormatStore: DateFormatStore,
  project?: Project,
) => {
  const createdAtDate = F.pipe(
    project?.createdAt,
    O.fromNullable,
    O.map(createdAt => F.pipe(createdAt, parseAppDateTime)),
  );

  const createdAtText = F.pipe(
    createdAtDate,
    O.map(
      F.flow(
        fns.format(`${dateFormatStore.format} ${dateFormatStore.fnsTimeFormat}`),
        it => `created: ${it}`,
      ),
    ),
  );

  const updatedAtDate = F.pipe(
    project?.updatedAt,
    O.fromNullable,
    O.map(parseAppDateTime),
  );

  const updatedAtText = F.pipe(
    updatedAtDate,
    O.filter(updatedAt =>
      F.pipe(
        createdAtDate,
        O.filter(createdAt =>
          F.pipe(createdAt, fns.addMinutes(1), fns.isBefore(updatedAt)),
        ),
        O.isSome,
      ),
    ),
    O.map(
      F.flow(
        fns.format(`${dateFormatStore.format} ${dateFormatStore.fnsTimeFormat}`),
        it => `last updated: ${it}`,
      ),
    ),
  );

  const textBlocks = F.pipe(
    [createdAtText, updatedAtText],
    RA.map(E.fromOption(() => undefined)),
    RA.separate,
    ({ right }) => (right.length > 0 ? ` [${right.join(', ')}]` : ''),
  );

  return `Project name${textBlocks}`;
};
